import { Link } from "react-router-dom";
import { Globe, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const SUPPORT_EMAIL = "info.nomadroadmap@gmail.com";

const Section = ({ n, title, children }) => (
  <section className="space-y-3">
    <h2 className="text-xl font-semibold text-foreground">{n}. {title}</h2>
    {children}
  </section>
);

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Globe className="h-6 w-6 text-primary" />
            <span className="font-bold text-lg font-['Manrope']">Nomad Roadmap</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm"><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-2 font-['Manrope']">Terms of Service</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: November 5, 2026</p>

        <div className="space-y-8 text-muted-foreground leading-relaxed">
          <p>
            These Terms of Service ("Terms") are an agreement between you and <span className="text-foreground font-medium">NomadRoadmap LLC</span> ("Nomad Roadmap", "we", "us"), the operator of nomadroadmap.com and the Nomad Roadmap service (the "Service"). By creating an account or using the Service you agree to these Terms and to our <Link to="/privacy" className="underline">Privacy Policy</Link>. If you do not agree, do not use the Service.
          </p>

          <Section n={1} title="What the Service is - and is not">
            <p>Nomad Roadmap is a planning and organization tool for people considering a move abroad. It helps you compare visa options, build checklists, store your documents and track deadlines.</p>
            <p><span className="text-foreground font-medium">We are not a law firm, immigration consultancy or tax adviser, and nothing in the Service is legal, immigration, tax or financial advice.</span> Visa rules, fees, income thresholds and processing times change often and vary by consulate. The information we show is compiled from public sources, may be out of date or incomplete, and must be verified against the official government or consular source before you rely on it. If your situation is complex, consult a licensed immigration attorney.</p>
          </Section>

          <Section n={2} title="No guarantee of outcome">
            <p>We do not guarantee that any visa, residence permit or application will be approved, that any timeline will be met, or that any requirement we list is complete. Decisions rest solely with the relevant government authorities.</p>
          </Section>

          <Section n={3} title="Eligibility and your account">
            <p>You must be at least 18 years old to use the Service. You are responsible for keeping your login details confidential, for all activity under your account, and for the accuracy of the information and documents you provide. Tell us immediately at {SUPPORT_EMAIL} if you believe your account has been accessed without permission.</p>
          </Section>

          <Section n={4} title="Plan, pricing and automatic renewal">
            <p>Nomad Roadmap is sold as one subscription plan, billed in US dollars through Stripe:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><span className="text-foreground">$29.99</span>, charged when you subscribe, covers your first 3 months (90 days).</li>
              <li>After the first 3 months, your subscription <span className="text-foreground font-medium">renews automatically at $6.99 per month</span>, charged to your payment method on each renewal date, until you cancel.</li>
            </ul>
            <p>Prices exclude any applicable taxes. We may change the monthly price with at least 30 days' notice by email; the new price applies from your next renewal after the notice period, and you may cancel before then.</p>
          </Section>

          <Section n={5} title="Cancellation">
            <p>You can cancel at any time from <span className="text-foreground">Profile → Manage billing</span>, which opens your Stripe billing portal, or by emailing {SUPPORT_EMAIL}. Cancelling stops future charges; your access continues until the end of the period you have already paid for and is not renewed after that. Deleting your account also cancels your subscription.</p>
          </Section>

          <Section n={6} title="Refunds">
            <p>If you are not satisfied, email {SUPPORT_EMAIL} within <span className="text-foreground">7 days</span> of your first $29.99 payment and we will refund it in full. After 7 days, and for monthly renewal payments, charges are non-refundable except where the law requires otherwise. Refunds are returned to the original payment method.</p>
          </Section>

          <Section n={7} title="Your content and documents">
            <p>You keep ownership of everything you upload. You grant us only the limited right to store, process and display it in order to provide the Service to you. You are responsible for having the right to upload what you upload. You can delete individual documents, or your whole account, at any time; see the Privacy Policy for how deletion works.</p>
          </Section>

          <Section n={8} title="Acceptable use">
            <p>You agree not to: use the Service for anything unlawful or to prepare fraudulent applications; share your account or resell access; attempt to probe, scrape, overload or reverse-engineer the Service; upload malicious code; or interfere with other users. We may suspend or terminate accounts that violate these Terms, with a refund of any unused prepaid period only where we choose to terminate without cause.</p>
          </Section>

          <Section n={9} title="Third-party services">
            <p>Payments are processed by Stripe, email is delivered by Resend, and data is hosted on MongoDB Atlas, Render and Vercel. Links to government and consular websites are provided for convenience; we do not control those sites.</p>
          </Section>

          <Section n={10} title="Disclaimer of warranties">
            <p>The Service is provided "as is" and "as available". To the fullest extent permitted by law we disclaim all warranties, express or implied, including fitness for a particular purpose, accuracy of information and uninterrupted availability.</p>
          </Section>

          <Section n={11} title="Limitation of liability">
            <p>To the fullest extent permitted by law, NomadRoadmap LLC and its members, officers and contractors will not be liable for any indirect, incidental, special, consequential or punitive damages, or for lost profits, visa denials, travel or relocation costs, missed deadlines or data loss, arising from your use of or inability to use the Service. Our total liability for any claim relating to the Service is limited to the amount you paid us in the 12 months before the claim arose.</p>
          </Section>

          <Section n={12} title="Changes to the Service or these Terms">
            <p>We may change the Service or these Terms. For material changes we will email the address on your account or show a notice in the Service at least 14 days before they take effect. Continuing to use the Service after that date means you accept the updated Terms.</p>
          </Section>

          <Section n={13} title="Governing law and disputes">
            <p>These Terms are governed by the laws of the State of Nevada, USA, without regard to conflict-of-law rules. Before filing any claim, you agree to contact us at {SUPPORT_EMAIL} and try in good faith to resolve the issue for 30 days. Any dispute that cannot be resolved will be brought in the state or federal courts located in Clark County, Nevada, and you consent to their jurisdiction. Nothing in this section limits rights you have as a consumer under the laws of the place where you live.</p>
          </Section>

          <Section n={14} title="Contact">
            <p>NomadRoadmap LLC<br />{SUPPORT_EMAIL}</p>
          </Section>
        </div>
      </main>
    </div>
  );
};

export default TermsPage;
